import type { ReactElement } from "react";
import { createPortal } from "react-dom";
import { SegmentedControl, type SegmentedControlOption } from "./SegmentedControl";
import FreshnessStamp, { type FreshnessStampProps } from "./FreshnessStamp";
import { TOPBAR_SLOT_ID, useTopBarSlot } from "./topBarSlots";
import TourButton from "./tour/TourButton";

export interface TopBarTabs<T extends string> {
  /** e.g. Overview / Sales reports. Plain names only. */
  options: SegmentedControlOption<T>[];
  value: T;
  onChange: (v: T) => void;
}

export interface TopBarProps<T extends string> {
  tabs?: TopBarTabs<T>;
  /** From the server's freshness read. Absent → no stamp at all. */
  freshness?: FreshnessStampProps;
  /** The page has a tour: the "?" button that replays it. */
  onTour?: () => void;
}

/** The page's top-bar cluster: page tabs, the freshness stamp, then the tour button. */
export function TopBarContent<T extends string>({ tabs, freshness, onTour }: TopBarProps<T>): ReactElement {
  return (
    <div className="flex min-w-0 items-center justify-end gap-5">
      {tabs != null && tabs.options.length > 1 && (
        <SegmentedControl variant="seg" ariaLabel="Page view" options={tabs.options} value={tabs.value} onChange={tabs.onChange} />
      )}
      {/* The mock drops the stamp from the bar at 860px and below. */}
      {freshness != null && (
        <span className="hidden nav:flex">
          <FreshnessStamp {...freshness} />
        </span>
      )}
      {onTour != null && <TourButton onClick={onTour} />}
    </div>
  );
}

/**
 * TopBar — a v2 page puts its tabs and freshness stamp into the Navbar
 * (handoff rulings 7 and 10). Renders into `#topbar-slot`; nothing when the
 * slot is absent.
 */
export default function TopBar<T extends string>(props: TopBarProps<T>): ReactElement | null {
  const slot = useTopBarSlot(TOPBAR_SLOT_ID);
  return slot ? createPortal(<TopBarContent {...props} />, slot) : null;
}
